import React, { useContext } from 'react';
import GlobalChatBoxV2 from '@nile/components/src/Components/GlobalChatBoxV2';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import {
  EventEmitter,
  getTranslatedText,
  useUserMSPCheckHook,
  COMMON_CONSTANTS
} from '@nile/components';
import { useIntl } from 'react-intl';
import envConfig from '@nile/home/src/Config/env';
import { useSideConversationActionStore } from '@nile/mangifera/src/StoreManager/store/sideConversationActionStore.tsx';
import { useSuperEditor } from '@nile/components/src/Components/RichTextEditor/useSuperEditor';
import { useLazyQuery } from '@apollo/client';
import {
  AccountClient,
  CREATE_VENDOR_CONVERSATION,
  GET_VENDOR_CONVERSATION_REPLY_RECIEPIENTS
} from '@nile/graphql';
import { dompurifier } from '@nile/components/src/Utils/helpers';
import {
  GET_ORIGINAL_MESSAGE,
  GET_DEFAULT_SIDE_CONVERSATION_REPLY_TEMPLATE
} from '@nile/graphql/src/Query/ticket';
import SendSideConversation from '@nile/components/src/Components/Conversations/SendSideConversation';
import SideConversationHeaderAction from './SideConversationHeaderAction';

const useSideConversationActions = ({ workItem, module, conversationId }) => {
  const tabContext = useContext(TabContext);
  const intl = useIntl();

  const workId = workItem?.id || workItem?.workId;

  const isNewConversation = !conversationId;

  const formattedWorkItem = {
    workId: workItem?.workId,
    module: workItem?.module || module
  };

  const isMSP = useUserMSPCheckHook();

  const SIDE_CONVERSATION_EDITOR_STORE_CONFIG = {
    id: workId,
    name: `${module}_${workId}_${conversationId || 'new'}_sideConversation`,
    cacheConfig: {
      scope: 'global'
    }
  };

  const [
    getRecipients,
    {
      loading: recipientsLoading,
      data: recipients,
      error: recipientsError,
      refetch: refetchRecipients
    }
  ] = useLazyQuery(GET_VENDOR_CONVERSATION_REPLY_RECIEPIENTS, {
    client: AccountClient,
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
  });

  const [
    getDefaultReplyTemplateContent,
    { loading: replyTemplateLoading }
  ] = useLazyQuery(GET_DEFAULT_SIDE_CONVERSATION_REPLY_TEMPLATE, {
    client: AccountClient,
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
  });

  const [
    getOriginalMessage,
    { loading: originalMessageLoading }
  ] = useLazyQuery(GET_ORIGINAL_MESSAGE, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
  });

  const {
    template,
    updateTemplate,
    resetTemplate,
    getTemplate
  } = useSuperEditor(SIDE_CONVERSATION_EDITOR_STORE_CONFIG);

  const config = {
    module: module,
    workId: workId,
    conversationId: conversationId || 'new'
  };

  const sideConversationActionStore = useSideConversationActionStore(config);

  const cc = sideConversationActionStore((state) => state.cc);
  const setCc = sideConversationActionStore((state) => state.setCc);
  const bcc = sideConversationActionStore((state) => state.bcc);
  const setBcc = sideConversationActionStore((state) => state.setBcc);
  const to = sideConversationActionStore((state) => state.to);
  const setTo = sideConversationActionStore((state) => state.setTo);
  const selectedMailbox = sideConversationActionStore(
    (state) => state.selectedMailbox
  );
  const setSelectedMailbox = sideConversationActionStore(
    (state) => state.setSelectedMailbox
  );

  const resetSideConversationStoreData = sideConversationActionStore(
    (state) => state.resetSideConversationData
  );
  const subject = sideConversationActionStore((state) => state.subject);
  const setSubject = sideConversationActionStore((state) => state.setSubject);

  const openTicket = () => {
    tabContext.openTab({
      module: 'TICKET',
      ticketId: workItem.id,
      data: {
        subject: workItem.subject,
        displayId: workItem.displayId,
        ticketId: workItem.id
      }
    });
  };

  const OPEN_FUNCTION_MAP = {
    TICKET: openTicket
  };

  const openWorkItem = () => {
    const openFunction = OPEN_FUNCTION_MAP?.[module];
    if (openFunction) {
      openFunction();
    }
  };

  const resetSideConversationData = () => {
    resetTemplate();
    resetSideConversationStoreData();
  };

  const formatUsers = (users: any[]) => {
    return (
      users?.map((user) => ({
        id: user.userId,
        name: user?.name,
        email: user.email,
        label: user?.name || user?.email,
        value: user?.name || user?.email
      })) || []
    );
  };

  const getDefaultRecipientsData = async () => {
    const storeData = sideConversationActionStore.getState();
    const currentMailbox = storeData?.selectedMailbox;

    const currentTo = storeData?.to;
    const currentCc = storeData?.cc;
    const currentBcc = storeData?.bcc;

    if (!isMSP) {
      return null;
    }
    if (isNewConversation && currentMailbox) {
      // For new side conversation
      // Check if the existing data is in the store and return
      return {
        selectedMailbox: currentMailbox
      };
    }
    if (
      !isNewConversation &&
      (currentTo || currentCc || currentBcc || currentMailbox)
    ) {
      // For existing conversation with existing data in store
      // Check if the existing data is in the store and return
      return {
        to: currentTo,
        cc: currentCc,
        bcc: currentBcc,
        selectedMailbox: currentMailbox
      };
    }
    const response = await getRecipients({
      variables: {
        input: {
          workItem: {
            workId: workId,
            module: module
          },
          conversation: conversationId
            ? {
                conversationId: conversationId
              }
            : undefined
        }
      }
    });

    const recipientData = response?.data?.getVendorConversationReplyRecipients;
    let selectedMailboxData = null;

    if (
      recipientData?.selectedMailbox?.outgoingDomainVerified &&
      !selectedMailbox
    ) {
      selectedMailboxData = {
        ...recipientData?.selectedMailbox,
        label: recipientData?.selectedMailbox?.outgoingAddress
      };
      setSelectedMailbox(selectedMailboxData);
    }

    if (isNewConversation) {
      // For new side conversation the TO, CC and Bcc will come from template
      return {
        selectedMailbox: selectedMailboxData
      };
    }

    const toUsers = formatUsers(recipientData?.to);
    const currentToUser = to?.length > 0 ? to : toUsers;
    setTo(currentToUser || null);
    const ccUsers = cc?.length > 0 ? cc : formatUsers(recipientData?.cc);
    setCc(ccUsers || []);
    const bccUsers = bcc?.length > 0 ? bcc : formatUsers(recipientData?.bcc);
    setBcc(bccUsers || []);

    return {
      to: toUsers,
      cc: ccUsers,
      bcc: bccUsers
    };
  };

  const getDefaultTemplateData = async ({ selectedMailBox, templateId }) => {
    const currentTemplate = getTemplate();
    if (currentTemplate) {
      return currentTemplate;
    }
    const variables = {
      input: {
        template: templateId
          ? {
              templateId
            }
          : undefined,
        workItem: {
          workId: workId,
          module: module
        },
        mailbox: selectedMailBox?.boxId
          ? {
              boxId: selectedMailBox?.boxId
            }
          : null
      }
    };

    const response = await getDefaultReplyTemplateContent({
      variables
    });

    let originalMessageContent;
    if (isNewConversation && module === 'TICKET') {
      const originalMessageResponse = await getOriginalMessage({
        variables: {
          input: {
            entityId: workId
          }
        }
      });
      if (originalMessageResponse?.data?.getTicketConversationThreadedContent) {
        originalMessageContent = `<p><br></p><p><br></p>${dompurifier(
          originalMessageResponse?.data?.getTicketConversationThreadedContent
        )}`;
      }
    }

    const defaultTemplateData =
      response?.data?.getVendorConversationContentFromTemplate;
    let update = null;

    if (isNewConversation) {
      const storeData = sideConversationActionStore.getState();

      const currentToUsers = storeData?.to;
      const currentCCUsers = storeData?.cc;
      const currentBccUsers = storeData?.bcc;

      if (!currentToUsers) {
        const toUsersFromTemplate = defaultTemplateData?.toUsers?.length
          ? defaultTemplateData?.toUsers?.map((el) => el?.user)
          : null;
        const formattedToUsers = formatUsers(toUsersFromTemplate || []);
        setTo(formattedToUsers);
      }

      if (!currentCCUsers) {
        const ccUsersFromTemplate = defaultTemplateData?.ccUsers?.length
          ? defaultTemplateData?.ccUsers?.map((el) => el?.user)
          : [];
        const formattedCCUsers = formatUsers(ccUsersFromTemplate || []);
        setCc(formattedCCUsers);
      }

      if (!currentBccUsers) {
        const bccUsersFromTemplate = defaultTemplateData?.bccUsers?.length
          ? defaultTemplateData?.bccUsers?.map((el) => el?.user)
          : [];
        const formattedBccUsers = formatUsers(bccUsersFromTemplate || []);
        setBcc(formattedBccUsers);
      }

      const currentSubject =
        sideConversationActionStore.getState().subject || '';
      if (!currentSubject) {
        setSubject(defaultTemplateData?.subject);
      }
    }

    if ((defaultTemplateData || originalMessageContent) && !template) {
      const defaultReplyContent = defaultTemplateData?.content || '';
      const attachments = defaultTemplateData?.attachments;
      let content = defaultReplyContent || '';
      if (originalMessageContent) {
        content = `${defaultReplyContent}${originalMessageContent}`;
      }
      if (content) {
        update = {
          content: dompurifier(content)
        };
      }
      if (attachments?.length) {
        update = {
          ...update,
          attachments: attachments
        };
      }
      if (update) {
        updateTemplate(update);
      }
    }
    return update;
  };

  const getRecipientsConfig = ({ recipientsData, isNewSideConversation }) => {
    const currentTo = sideConversationActionStore.getState().to;
    const currentCc = sideConversationActionStore.getState().cc;
    const currentBcc = sideConversationActionStore.getState().bcc;

    const toConfig = isNewSideConversation
      ? {
          value: currentTo,
          isMailList: true,
          onChange: setTo,
          config: {
            placeholder: getTranslatedText(
              intl,
              'app.sideConversation.placeHolder.mail.recipients'
            )
          }
        }
      : {
          value: recipientsData?.to,
          showOnlyEmail: true
        };
    return {
      to: toConfig,
      cc: {
        value: currentCc || recipientsData?.cc,
        isMailList: true,
        onChange: setCc,
        config: {
          placeholder: getTranslatedText(
            intl,
            'app.sideConversation.placeHolder.mail.recipients'
          )
        }
      },
      bcc: {
        value: currentBcc || recipientsData?.bcc,
        isMailList: true,
        onChange: setBcc,
        config: {
          placeholder: getTranslatedText(
            intl,
            'app.sideConversation.placeHolder.mail.recipients'
          )
        }
      }
    };
  };

  const openChatPopup = async (params) => {
    const templateId = params?.templateId;
    const defaultRecipientsData = await getDefaultRecipientsData();
    const selectedMailboxData = defaultRecipientsData?.selectedMailbox || null;
    const defaultTemplateData = await getDefaultTemplateData({
      selectedMailBox: selectedMailboxData,
      templateId
    });

    const recipientsConfig = getRecipientsConfig({
      recipientsData: defaultRecipientsData,
      isNewSideConversation: !conversationId
    });

    const currentSubject = sideConversationActionStore.getState().subject || '';

    const CHAT_PARAMS = {
      key: isNewConversation
        ? `new_side_conversation_${workId}`
        : `side_conversation_${workId}`,
      id: isNewConversation ? `new_side_conversation_${workId}` : workId,
      subject: workItem?.subject || 'SUBJECT',
      module: workItem?.module,
      component: GlobalChatBoxV2,
      type: 'GLOBAL_CHAT',
      components: {
        headerAction: {
          component: SideConversationHeaderAction
        },
        primaryAction: {
          component: SendSideConversation
        }
      },
      data: {
        heading: getTranslatedText(intl, 'common.side.conversation'),
        icon: 'Helpcenter',
        mode: isNewConversation ? 'NEW_SIDE_CONVERSATION' : 'SIDE_CONVERSATION',
        iconBg: 'info',
        workItem: formattedWorkItem,
        isNewConversation,
        conversationId: conversationId || 'new'
      },
      actions: {
        onSubjectClick: openWorkItem,
        onClose: resetSideConversationData
      },
      config: {
        recipients: recipientsConfig,
        subject: isNewConversation
          ? {
              value: currentSubject,
              onChange: setSubject
            }
          : null,
        editor: {
          placeholder: getTranslatedText(intl, 'common.placeholder.writeHere'),
          details: {
            workItem: {
              module: module,
              workId: workId
            }
          },
          focusEditor: true,
          module: 'VENDOR_CONVERSATION',
          allowMentions: false,
          attachmentUploadUrl: `${envConfig.fileRoot}/upload`,
          uploadParams: {
            module: 'VENDOR_CONVERSATION_ATTACHMENT',
            details: JSON.stringify({
              workItem: {
                workId: workId,
                module: module
              }
            })
          },
          storeConfig: SIDE_CONVERSATION_EDITOR_STORE_CONFIG
        }
      }
    };
    EventEmitter.dispatch('createGlobalConversation', CHAT_PARAMS);
  };

  return {
    openChatPopup,
    loading: recipientsLoading || replyTemplateLoading
  };
};

export default useSideConversationActions;
